<template>
  <div>
    <p v-if="!isLoading && exceptions.length === 0" class="tw:text-sm tw:text-zinc-500 tw:m-0">
      {{ t('No event departs from your general preferences.') }}
    </p>

    <div v-else class="tw:flex tw:flex-col tw:gap-5">
      <div
        v-for="exception in exceptions"
        :key="exception.id"
        class="tw:flex tw:items-center tw:gap-3"
      >
        <div class="tw:flex tw:items-center tw:justify-center tw:w-9 tw:h-9 tw:rounded-lg tw:bg-primary/10 tw:text-primary tw:shrink-0">
          <Icon :name="exception.type_icon" />
        </div>

        <div class="tw:flex tw:flex-col tw:min-w-0 tw:grow">
          <a
            v-if="exception.subscribable_url"
            :href="exception.subscribable_url"
            class="tw:text-sm tw:font-medium tw:text-primary tw:truncate tw:hover:underline"
          >
            {{ exception.subscribable_name }}
          </a>
          <span v-else class="tw:text-sm tw:font-medium tw:truncate">
            {{ exception.subscribable_name ?? t('Unknown event') }}
          </span>

          <span class="tw:text-[11px] tw:uppercase tw:tracking-wider tw:text-zinc-500 tw:font-semibold">
            {{ exception.type_label }} &middot;
            {{ exception.subscribed ? t('Receive') : t('Do not receive') }}
          </span>
        </div>

        <button
          type="button"
          data-testid="remove"
          class="tw:inline-flex tw:items-center tw:justify-center tw:w-9 tw:h-9 tw:shrink-0 tw:rounded-lg tw:border tw:border-zinc-200 tw:dark:border-zinc-800 tw:bg-transparent tw:text-zinc-500 tw:transition tw:cursor-pointer tw:hover:border-red-500 tw:hover:text-red-600 tw:disabled:opacity-50"
          :disabled="removing[exception.id]"
          :aria-label="t('Remove this exception')"
          :title="t('Remove this exception')"
          @click="remove(exception)"
        >
          <Icon name="Trash2" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import axios from 'axios'
import { Icon } from '@/components/ui/icon'
import { toast } from 'vue-sonner'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const api = (path) => `${window.Laravel.backofficeApi}/${path}`

const exceptions = ref([])
const isLoading = ref(true)
const removing = ref({})

const load = async () => {
  try {
    const { data } = await axios.get(api('notification-subscription'), {
      params: { filter: { global: 0 } },
    })
    exceptions.value = data.data
  } catch (error) {
    console.error('Failed to load notification exceptions:', error)
    toast.error(t('Failed to update notification subscription'))
  } finally {
    isLoading.value = false
  }
}

/**
 * Removing an exception is not an unsubscribe: it drops the answer given about that
 * event, which puts it back in step with the general preference.
 */
const remove = async (exception) => {
  removing.value[exception.id] = true

  try {
    await axios.delete(api('notification-preference'), {
      data: {
        type: exception.type,
        subscribable_type: exception.subscribable_type,
        subscribable_id: exception.subscribable_id,
      },
    })
    exceptions.value = exceptions.value.filter((item) => item.id !== exception.id)
    toast.success(t('Notification preference saved'))
  } catch (error) {
    console.error('Failed to remove exception:', error)
    toast.error(t('Failed to update notification subscription'))
  } finally {
    removing.value[exception.id] = false
  }
}

onMounted(load)
</script>
